import CustomError from '../customError';
import { MessageTypes } from '../types/messageTypes';
import MessageHelper from './messageHelper';

// TP-2327: every scanned document in the api answers with these two codes, not just the letterhead.
const HTTP_STATUS_SCAN_PENDING = 423; // Locked: the document is still being scanned
const HTTP_STATUS_SCAN_GONE = 410;    // Gone: the scan rejected or removed the document

// The reason phrases the api sends for a letterhead (TiaApiProviderException.DefaultReasonphrases).
// They are the only thing that tells a refused export apart from any other refused document.
const API_INVOICE_TEMPLATE_SCAN_PENDING = 'Invoice template scan in progress. Try again later.';
const API_INVOICE_TEMPLATE_SCAN_INFECTED = 'Invoice template was identified as malicious and is no longer available.';

class ErrorHelper {
    static getCustomWebsiteErrorFunction() {
        return (error: any) => {
            throw this.GetCustomErrorObject(error);
        };
    }

    static getCustomWebsiteBlobErrorFunction() {
        return async (error: any) => {
            if (error?.response?.body instanceof Blob) {
                try {
                    const text = await error.response.body.text();
                    error.response.body = JSON.parse(text);
                } catch {
                    // body niet parseerbaar (bv. HTML error page) - laat zoals is, fallback path treedt op
                }
            }
            throw this.GetCustomErrorObject(error);
        };
    }

    static showMessageNoRethrow(error: any) {
        const customError = this.GetCustomErrorObject(error);
        MessageHelper.showMessage({type: customError.messageType, message: customError.message});
    }

    static showMessageOnNoContent(customError: CustomError) {
        if(customError.messageType === MessageTypes.INFO)
        {
            MessageHelper.showMessage({type: customError.messageType, message: customError.message});
        }
    }

    private static buildScanRetryHint(error: any) : string {
        const retryAfterSeconds = parseInt(error?.response?.headers?.['retry-after'], 10);
        if(Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0) {
            const minutes = Math.ceil(retryAfterSeconds / 60);
            return `Please try again in about ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}.`;
        }
        return 'Please try again shortly.';
    }

    // The api serialises a TiaApiException body in PascalCase (no camelCase resolver in Startup.cs), which is
    // why the generic body-check in GetCustomErrorObject misses it.
    private static getApiReasonPhrase(error: any) : string | null {
        const body = error?.response?.body;
        if(!body) return null;

        const errors = body.Errors ?? body.errors;
        const firstError = Array.isArray(errors) ? errors[0] : errors;
        return firstError?.Message ?? firstError?.message ?? body.Title ?? body.title ?? null;
    }

    // The letterhead is the one scanned document the website words itself: its refusal blocks a pdf export,
    // not a download. Anything else keeps the api's phrase, so an attachment is never reported as a letterhead.
    private static buildScanPendingMessage(error: any) : string {
        const reasonPhrase = this.getApiReasonPhrase(error);
        if(reasonPhrase === API_INVOICE_TEMPLATE_SCAN_PENDING) {
            return `The letterhead is still being scanned. ${this.buildScanRetryHint(error)}`;
        }
        return reasonPhrase ?? `The document is still being scanned. ${this.buildScanRetryHint(error)}`;
    }

    private static buildScanGoneMessage(error: any) : string {
        const reasonPhrase = this.getApiReasonPhrase(error);
        if(reasonPhrase === API_INVOICE_TEMPLATE_SCAN_INFECTED) {
            return 'The letterhead is no longer available, so the export could not be generated.';
        }
        return reasonPhrase ?? 'The document is no longer available.';
    }

    static errorHasStatusCode(error: any, ...statusCodes: number[]) : boolean {
        return statusCodes.map(s => s?.toString()).includes(error?.toString()) ||
               statusCodes.map(s => s?.toString()).includes(error?.status?.toString()) ||
               statusCodes.map(s => s?.toString()).includes(error?.message?.toString());
    }

    private static GetCustomErrorObject(error: any) : CustomError
    {
        // Already mapped: the api layer throws a CustomError and the sagas hand that same object to
        // showMessageNoRethrow. A second pass would only overwrite a correct result, because the status is
        // still 423/410 while the response - and with it the Retry-After header and the body - is gone.
        if(error instanceof CustomError) return error;

        const customError = new CustomError(MessageTypes.ERROR, 'Unknown error occurred.');
        if(error) {
            if(error.status) customError.status = error.status;

            // TP-2327: turn the scan-status codes into a readable message instead of the bare status code.
            // Pending is transient and self-resolving, hence INFO rather than the default error styling.
            if(this.errorHasStatusCode(error, HTTP_STATUS_SCAN_PENDING)) {
                customError.messageType = MessageTypes.INFO;
                customError.message = this.buildScanPendingMessage(error);
                return customError;
            }

            if(this.errorHasStatusCode(error, HTTP_STATUS_SCAN_GONE)) {
                customError.message = this.buildScanGoneMessage(error);
                return customError;
            }

            const response = error.response;
            if(response) {
                if (response.body) {
                    const { error: bodyError, errors: bodyErrors } = response.body;

                    if (bodyError) {
                        customError.message = bodyError;
                        return customError;
                    }

                    if (bodyErrors) {
                        customError.message = Array.isArray(bodyErrors)
                            ? bodyErrors.map(e => e.message || e).join('\r\n')
                            : bodyErrors.message || bodyErrors;

                        return customError;
                    }
                }

                if(response.noContent | response.badRequest | response.notFound) {
                    customError.messageType = MessageTypes.INFO;

                    if(response.noContent) {
                        customError.message = 'No content';
                        return customError;
                    }

                    if(response.badRequest) {
                        customError.message = 'Bad request';
                        return customError;
                    }

                    if(response.notFound) {
                        customError.message ='Not found';
                        return customError;
                    }
                }

                if(error.status) {
                    customError.message = error.status;
                    return customError;
                }
            } else if(error.status) {
                customError.status = error.status;
                if(error.message) customError.message = error.message;
                if(!error.message) customError.message = error.status;
                return customError;
            } else if(error.message) {
                customError.message = error.message;
                return customError;
            }
        }
        return customError;
    }
}

export default ErrorHelper;
